/*
 * BuroEcl
 * Copyright (c) 2023 Buroclic. All rights reserved.
 * 
 * This file corresponds to the display of the content of a Chemise,
 * including breadcrumb, chemise menu and documents
 */

// Imports
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import PropTypes from "prop-types";
import { toast } from 'react-toastify';
import { faDownload } from '@fortawesome/free-solid-svg-icons';

// Interfaces
import { PublicCabinet } from '../../interfaces/ICabinets';
import { Dossier, LevelChemise, Mission, Chemise } from "../../interfaces/IDossiers";
import { Document } from '../../interfaces/IDocuments';
import { AlertType, SortOrder, SortType, TableRow } from '../../interfaces/IUtils';

// Subpage content
import { Breadcrumb } from "./subcomponents/BreadCrumb";

// Utils
import { ApiCallConstructor, HTTPResponse, downloadDocument } from '../utils/ApiCallConstructor';
import { ErrorForUsr, displayAlert, signOut } from '../utils/Errors';
import { dateToDDMMYYYY, getExtFromFilename, getTypeImg } from '../utils/Format';

// Buroclic adaptive components
import { BCTable } from '../buroclic/BCTable';

export const ChemiseDetails = (props) => {
    const navigate = useNavigate();

    const cabinet = (JSON.parse(localStorage.getItem('cabinet') || "{}") as PublicCabinet);

    const treeviewChemises: Array<Chemise> = props.treeviewChemises;

    // current data
    const dossier: Dossier = props.dossier;
    const mission: Mission = props.mission;
    const [chemise, setChemise] = useState<Chemise>(props.chemise || {} as Chemise); // current chemise
    const [sousChemises, setSousChemises] = useState<Chemise[]>(props.chemise.children || [] as Array<Chemise>); // sous-chemise of current chemise
    const [chemisePath, setChemisePath] = useState<LevelChemise[]>([] as Array<LevelChemise>); // Breadcrumb of the current chemises

    const [documents, setDocuments] = useState<Document[]>([]);

    useEffect(() => {
        // Get sous-chemises to display
        setSousChemises((chemise && chemise.children) ? chemise.children : [] as Array<Chemise>);

        // Get chemise path as an Array of LevelChemise in order for the breadcrumb to work
        const pathRefs = chemise.PAR_REF_CHEMIN?.match(/[0-9]+/gmi);
        const pathNoms = chemise.PAR_CHEMIN?.match(/[^\\]+/gmi);
        const tmpChemisePath = [] as Array<LevelChemise>;

        // Construct the LevelChemise with name and PAR_REF
        if (pathRefs && pathNoms) {
            for (let i = 0; i < pathRefs?.length; i++) {
                tmpChemisePath.push({
                    id: Number(pathRefs[i]),
                    nom: pathNoms[i],
                    mission: mission.MIS_REF > 0 ? mission.MIS_CODE + ' - ' + mission.MIS_LIBELLE : ''
                });
            }
        }
        setChemisePath(tmpChemisePath);


        // Get documents contained in the provided chemise in order to fill the table
        const fetchDocuments = async () => {
            try {
                const getDocumentsResponse: HTTPResponse = await toast.promise(
                    ApiCallConstructor({
                        endpoint: `/me/dossiers/${dossier.DOS_REF}/chemises/${chemise.PAR_REF}/documents`,
                        method: 'GET'
                    }),
                    {
                        pending: `Récupération des documents en cours`,
                        error: { render({ data }) { return `Erreur lors de la récupération des documents : ${(data as HTTPResponse).error}` } }
                    }
                );

                // Store documents
                setDocuments(getDocumentsResponse.result as Array<Document>);
            } catch (err: any) {
                // Handle redirection for session expiration
                if (err.error === ErrorForUsr.NotSignedIn)
                    signOut((cabAlias) => navigate(`/${cabAlias}/login`));

                console.error(err.error)
                throw err;
            }
        }

        // get documents
        if (dossier.DOS_REF && chemise.PAR_REF)
            fetchDocuments().catch((err) => { displayAlert({ message: err.error, type: AlertType.error }); });
    }, [chemise, dossier, mission, navigate, treeviewChemises]);


    useEffect(() => {
        if (!chemise.PAR_REF && mission.MIS_REF) {
            setSousChemises(treeviewChemises.filter((element) => { return (element.MIS_REF === mission.MIS_REF) && (element.PAR_REFPERE === 0 || element.PAR_REFPERE === null) }));
        }
    }, [mission, chemise, treeviewChemises]);

    /**
     * Look for a specific Chemise through its PAR_REF
     * @param {Array<Chemise>} haystackChemises 
     * @param {Number} parRef 
     * @returns 
     */
    const findChemiseByParRef = (haystackChemises : Array<Chemise>, parRef): Chemise | null => {
        for (const subChemise of haystackChemises) {
            // Fouund at Top-level
            if (subChemise.PAR_REF === Number(parRef)) return subChemise;

            if (subChemise.children && subChemise.children.length > 0) {
                const foundChemise :Chemise | null = findChemiseByParRef(subChemise.children, parRef);
                if (foundChemise) return foundChemise;
            }
        }
        return null;
    }

    const setChemiseById = (parRef) => {
        if (parRef > 0) setChemise(findChemiseByParRef(treeviewChemises, parRef) || {} as Chemise);
        else props.chemiseHandler({} as Chemise);
    }


    /* 
    Documents relative state / functions
    */

    // Documents Table data
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [tableRows, setTableRows] = useState<TableRow[]>([]);
    useEffect(() => {
        // Format documents as wanted by BCTable
        const tmpRows: TableRow[] = [];
        documents.forEach(document => {
            tmpRows.push({
                'id': document.DOC_REF,
                'Date': document.DOC_DATE,
                'Type': getExtFromFilename(document.DOC_FILE),
                'Nom': document.DOC_NOM || ''
            });
        });
        setTableRows(tmpRows);
    }, [documents]);

    // Download documents the user has selected
    // BE AWARE : Downloading more than 10 documents at the same time seems to
    // skip the download of some of the wanted documents. In order to prevent that
    // You will have to uncomment the timeout of 1s every 10 downloads
    // But this is useful only if the pagination is showing more than 10 documents at a time
    const DownloadSelectedDocs = async (selectedRow: TableRow[]) => {
        if (isDownloading) return; // Prevent multiple download

        try {
            // Must download a SELECTED document
            if (selectedRow.length <= 0)
                return;

            setIsDownloading(true);

            // Download each document one by one
            await toast.promise(Promise.all(
                selectedRow.map(async row => {
                    try {
                        await downloadDocument(documents.filter(doc => doc.DOC_REF === row.id)[0], navigate)
                    } catch (error) {
                        throw error;
                    }
                })
            ),
                {
                    pending: 'Préparation de votre téléchargement',
                    error: { render({ data }) { return `Erreur : ${(data as HTTPResponse).error}` } },
                    success: 'Document(s) téléchargé(s) avec succès'
                });
        } catch (error) {
            console.error(error);
        }

        setIsDownloading(false);
    };


    // JSX Template
    return (
        <div className={`h-full w-full flex flex-col shadow-md`}>
            {/* Breadcrumb */}
            <Breadcrumb key={`path-${(chemise && chemise.PAR_REF) ? chemise.PAR_REF : mission.MIS_REF}`} chemisePath={chemisePath} mission={mission} handleChemiseEdit={setChemiseById}></Breadcrumb>

            {/* Chemise content */}
            <div className="flex flex-row w-full h-full" style={{ boxShadow: 'rgba(0, 0, 0, 0.1) 0px -1px 15px -3px !important' }}>


                {/* Sous chemise menu */}
                {sousChemises.length > 0 &&
                    <div className="bg-[#FBFBFB] flex flex-col shadow-md min-w-[200px]">
                        <span className="mx-3 text-left my-2 font-bold">{chemise.PAR_NOM ? chemise.PAR_NOM : mission ? mission.MIS_CODE + ' - ' + mission.MIS_LIBELLE : ''}</span>
                        <hr className={`mx-3 my-0 border-2 opacity-100 rounded-lg`} style={{ borderColor: cabinet.color }} />

                        <ul className="pl-0">
                            {sousChemises.map((sousChemise, index) => (
                                <div key={`sous-chemise-${sousChemise.PAR_REF}`}>
                                    <li className="mt-2 pl-4 py-2 cursor-pointer hover:bg-orange-200" tabIndex={40 + index}
                                        onClick={() => { props.chemiseHandler(sousChemise); setChemise(sousChemise) }} key={`sous-chemise-${sousChemise.PAR_REF}`}>
                                        {sousChemise.PAR_NOM}
                                    </li>
                                    <hr className={`mx-3 my-0 border-1 opacity-50`} style={{ borderColor: cabinet.color }} />
                                </div>
                            ))}
                        </ul>
                    </div>
                }

                {/* Documents */}
                <div className="flex flex-column grow m-4">
                    <BCTable key={'documents-' + tableRows.length} emptyMsg={'Aucun document n\'a été publié dans cette chemise'} paginationMsg={'Document [0] à [1] sur [2]'} sortSettings={{ colName: 'Date', order: SortOrder.desc }}
                        columns={[
                            { 'name': 'id', show: false }, // ID
                            { 'name': 'Date', show: true, className: 'text-left w-[100px]', formatBeforeRender: (row: TableRow, colKey: string) => dateToDDMMYYYY(row[colKey]), sort: SortType.Date, usedForResearch: true },
                            { 'name': 'Type', show: true, className: 'text-left w-[80px]', formatBeforeRender: (row: TableRow, colKey: string) => getTypeImg(row[colKey], true), sort: SortType.Alphanumeric },
                            { 'name': 'Nom', show: true, className: 'text-left', sort: SortType.Alphanumeric, usedForResearch: true }
                        ]}
                        data={tableRows} selection={true} searchbar={true} className={'table-auto mt-2'}
                        buttons={[{ name: 'Télécharger', icon: faDownload, action: (selectedRows: TableRow[]) => { DownloadSelectedDocs(selectedRows); } }]} />

                </div>

            </div>
        </div >
    );
};

ChemiseDetails.propTypes = {
    dossier: PropTypes.object.isRequired,
    chemise: PropTypes.object.isRequired,
    treeviewChemises: PropTypes.arrayOf(PropTypes.object).isRequired,
    chemiseHandler: PropTypes.func.isRequired,
    mission: PropTypes.object.isRequired,
    className: PropTypes.string
}